import { Component, Input, OnInit, Output, ViewChild, EventEmitter, AfterViewInit } from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular';
import { NotificationService } from 'src/app/common/component/notification/notification.service';
import { CrmDataByZipTerritoryService } from '../crm-data-by-zip-territory.service';
import { CRMReportRequest } from '../crm-data-by-zip-territory.component';
import { column_definitions, DataGridCreatedEvent, isDecompressRequired } from './crm-report-datagrid-defns';
import { Observable, first, timer } from 'rxjs';
import { Utility } from 'src/app/common/service/utility';
import { TextDataResponse } from 'src/app/common/model/TextDataResponse';
import { BatchReportsService } from '../../batch-reports-grid/batch-reports-service';
import { v4 as uuidv4 } from 'uuid';
import { environment } from 'src/environments/environment';


@Component({
  selector: 'app-crm-report-datagrid',
  templateUrl: './crm-report-datagrid.component.html',
  styleUrls: ['./crm-report-datagrid.component.scss'],
})
export class CrmReportDatagridComponent implements OnInit, AfterViewInit {


  /*
    This is a generic re-usable component to generate a DevExtreme data-grid for CRM Reports.
    1. It receives the report-type and report-filters (CRMReporRequest) as input from the parent component.
    2. Thne it calls a service method to call back-end API and fetch the report data.
    3. Then it adds column definitions to the datagrid as defined in the configuration file - crm-report-datagrid-defns.ts
    4. First, the static column definitions are added. Then it executes the dynamic column generating function (if defined).
        a. Static column definitions = Columns that are pre-defined and fixed for each request, irrespective of backend data.
        b. Dynamic column definitions = Columns that are added based on data from back-end. Vary based on request.
    5. Then it executes the function to generate grouping columns aka banding columns (if defined).
    6. Then the data is loaded into datagrid and the UI is rendered.
    7. Finally an event is emitted to parent component to notify that the datagrid is created (so parent can enable export-to-excel etc.)
  */
  @Input()
  reportRequest: CRMReportRequest;
  
  @Input()
  reportType: string;

  @Output() dataGridCreated = new EventEmitter<DataGridCreatedEvent>();


  isLoadPanelVisible: boolean = false;
  crmReportData: any;
  readonly allowedPageSizes = [50, 100, 200];
  totalResultRowCount: number = 0;
  isExportButtonDisabled = true;
  @ViewChild(DxDataGridComponent, { static: false }) dataGrid: DxDataGridComponent;  

  retryEnabled: boolean = false;
  maxRetries = 1;
  retryCount = 0;

  constructor(private notificationService: NotificationService, 
    private crmDataByZipTerritoryService: CrmDataByZipTerritoryService, 
    private utility: Utility, private batchReportsService: BatchReportsService) {
    
  }

  ngOnInit(): void {

    if(this.reportRequest.majorSegments.length == 0){ //this means it is an all-segments request
      this.retryEnabled = true; //enable retry logic if it is a gateway timeout error
    }

    this.getCRMReport();
  }

  ngAfterViewInit(): void {
    this.dataGrid.instance.beginCustomLoading("");
  }

  //Get the data from back-end, add column definitions to data-grid, load the data, and send the grid-created event to parent.
  getCRMReport() {

    //Determine if the backend call needs to execute as a job.
    let {execAsJob, reportId} = this.crmDataByZipTerritoryService.shouldExecuteAsJob(this.reportType, this.reportRequest);

    //If local, don't execute as job. Comment this line only if local testing needs to be done.
    if(environment.apiBaseUrl.includes('localhost')) execAsJob = false;

    //In any case, define the observer object which will handle the backe-end data.
    //Whether it is a job execution or real-time execution, the returned data is the same.
    //So this observer will be attached either to the job status observable or the real-time HTTP call's observable.    
    let crmReportObserver = {
      next: async crmReportData => {

        if(!execAsJob && isDecompressRequired(this.reportType)){
          var decompressedData = this.utility.decompressData(crmReportData as TextDataResponse);
          crmReportData = JSON.parse(decompressedData);

          if(typeof crmReportData == 'string') {
            crmReportData = JSON.parse(crmReportData);
          }

          this.crmReportData = crmReportData;

        }
        let colDefs = await this.addColumnDefsToDataGrid(crmReportData, this.reportType);                    

        this.crmReportData = crmReportData;
        this.dataGrid.instance.endCustomLoading();          

        this.sendDataGridCreatedEvent(colDefs);

      },
      error: (errorResponse: any) => {

        if(errorResponse.status == 504 && this.retryEnabled && !execAsJob){ // if gateway timeout error and retry enabled and it is not a job.
          if(this.retryCount < this.maxRetries){ // attempt a retry if max retries not reached
            this.retryCount++;
            this.getCRMReport();
            return;
          }else { //max retries reached. disable retries.
            this.retryEnabled = false;
          }
        }

        this.dataGrid.instance.endCustomLoading();
        this.notificationService.alert("error", "Error while fetching CRM Report data", errorResponse);
        
      }
    }

    if(execAsJob){ //Submit the job      
      this.batchReportsService.submitJob(reportId, uuidv4(), this.crmDataByZipTerritoryService.prepareRequest(this.reportRequest))
        .subscribe({
          next: jobInfo => {
            if(jobInfo.status == "Submitted"){ //if submitted, get an observable that monitors job, attach the observer to handle response data.              
              //added the 'first' operator here. this is to prevent duplicate emits from observable (happens bcos of network/system slowness 
              //and one status check goes out before previous status check completes ).
              this.batchReportsService.getJobObservable(jobInfo.jobID).pipe(first()).subscribe(crmReportObserver);              
            }else{
              this.dataGrid.instance.endCustomLoading();
              this.notificationService.alert("error", "Error while fetching CRM Report data", {});
            }
          },
          error: errorResponse => {
            this.dataGrid.instance.endCustomLoading();
            this.notificationService.alert("error", "Error while fetching CRM Report data", errorResponse);
          }
        });
    }else{ // Else, call the service method to make HTTP call and attach the observer to handle response data.
      this.crmDataByZipTerritoryService.getCRMReport(this.reportRequest, this.reportType).subscribe(crmReportObserver);
    }

  }

  //This method adds column definitions to datagrid before data is loaded and rendered.
  async addColumnDefsToDataGrid(gridData: Array<Object>, reportType: string){

    //Add static column definitions for this reportType.
    let columnDefn = column_definitions.filter(defn => defn.datagridName == this.reportType)[0];    

    let staticColDefns = columnDefn.staticColumnDefs;

    for(let column of staticColDefns){
      let {formatString, ...gridColumn} = column;
      gridColumn["cssClass"] = "defaultHeaderAlignClass";
      this.dataGrid.instance.addColumn(gridColumn);
    }

    //Execute the function to generate dynamic columns for this reportType.
    //Add them to the datagrid.
    let dynamicColDefns = new Array<any>();
    
    if(columnDefn.dynamicColsFunc){
      let result = columnDefn.dynamicColsFunc(gridData, reportType);

      if(result.dynamicColumns){

        dynamicColDefns = result.dynamicColumns;

        for(let column of dynamicColDefns){
          let {formatString, ...gridColumn} = column;
          gridColumn["cssClass"] = "defaultHeaderAlignClass";
          this.dataGrid.instance.addColumn(gridColumn);
        }
      }      
      
      if(result.groupingColumns){
        this.dataGrid.customizeColumns = columnDefn.customizeColsFunc;
      }
    }    

    return {staticColDefns: staticColDefns, dynamicColDefns: dynamicColDefns};
    
  }

  resetFilters() {
    this.dataGrid.instance.clearFilter();
    this.dataGrid.instance.clearSorting();
  }

  // This method is to get total no of CURRENT row count in the grid
  // A function that is executed when the UI component is rendered and each time the component is repainted.
  onContentReady(eventName: any) {
    this.totalResultRowCount = eventName.component.totalCount();
  }


  //This method sends the datagrid created event to parent.
  //It passes the view-ref of datagrid component itself and the details of column formatting applied.
  //These information will be used by the parent component while exporting to excel.
  sendDataGridCreatedEvent(colDefs: any){
    
    let columns = [...colDefs.staticColDefns, ...colDefs.dynamicColDefns];

    let columnFormatDetails: Array<Object> = new Array<Object>();

    columns.forEach((col, i) => {
      if(col.formatString){        
        let columnNumber = i + 1;
        let gridFmt = col.formatString;
        columnFormatDetails.push({col: columnNumber, gridFmt: gridFmt});
      }
    });    

    this.dataGridCreated.emit({reportType: this.reportType, 
      dataGridComponent: this.dataGrid, 
      columnFormatDetails: columnFormatDetails,
      totalRowCount: this.crmReportData.length
    });
  }


}


